import dayjs, {Dayjs} from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import LegacyAccess from "@/core/interfaces/Access";
import {
    StaffingTarget,
    TeamAbsence,
    TeamMemberSchedule,
    TeamShiftException
} from "@/core/interfaces/TeamPlanning";
import {jobRoleForRank, rangeMinutes, TimeRange, toMinutes} from "@/team/planning";

dayjs.extend(isoWeek);

/** Mirror of TeamMemberProfile::ROTATION_EPOCH on the api-v2 and HDS sides. */
const ROTATION_EPOCH = dayjs('2025-12-22');

/**
 * Week of the rotation cycle a date falls in — the mirror of the api-v2
 * resolver TeamMemberProfile::weekIndexForDate(). The cycle counts the weeks
 * elapsed since ROTATION_EPOCH, so it runs on without a break across years,
 * ISO 53-week years included.
 */
export const weekIndexForDate = (date: Dayjs, rotationWeeks: number): number => {
    const weeks = Math.max(rotationWeeks, 1);
    const weeksElapsed = date.startOf('isoWeek').diff(ROTATION_EPOCH, 'week');

    return ((weeksElapsed % weeks) + weeks) % weeks;
};

/** How one member's day resolved, exceptions and absences applied. */
export interface MemberDay {
    member: LegacyAccess;
    date: Dayjs;
    ranges: TimeRange[];
    /** Approved absence masking the whole day, when any. */
    absence: TeamAbsence | null;
    /** Dated override the ranges come from, when any. */
    exception: TeamShiftException | null;
}

const coversDate = (absence: TeamAbsence, date: Dayjs): boolean =>
    !date.isBefore(dayjs(absence.starts_at), 'day') && !date.isAfter(dayjs(absence.ends_at), 'day');

/**
 * Resolve what a member actually does on a calendar date: an approved
 * absence masks the day, else a dated exception replaces it (null slots =
 * off), else the recurrence week matching the ISO parity applies.
 */
export const resolveMemberDay = (
    member: LegacyAccess,
    schedule: TeamMemberSchedule | undefined,
    exceptions: TeamShiftException[],
    absences: TeamAbsence[],
    date: Dayjs
): MemberDay => {
    const day: MemberDay = {member, date, ranges: [], absence: null, exception: null};

    day.absence = absences.find(absence =>
        absence.user_access_point_id === member.id
        && absence.status === 'approved'
        && coversDate(absence, date)
    ) ?? null;

    if (day.absence) {
        return day;
    }

    const isoDate = date.format('YYYY-MM-DD');
    day.exception = exceptions.find(exception =>
        exception.user_access_point_id === member.id && exception.date === isoDate
    ) ?? null;

    if (day.exception) {
        day.ranges = [...(day.exception.slots ?? [])].sort((a, b) => toMinutes(a.starts_at) - toMinutes(b.starts_at));

        return day;
    }

    if (!schedule) {
        return day;
    }

    const weekIndex = weekIndexForDate(date, schedule.rotation_weeks);

    day.ranges = schedule.shifts
        .filter(shift => shift.week_index === weekIndex && shift.day_of_week === date.day())
        .map(shift => ({starts_at: shift.starts_at.slice(0, 5), ends_at: shift.ends_at.slice(0, 5)}))
        .sort((a, b) => toMinutes(a.starts_at) - toMinutes(b.starts_at));

    return day;
};

export const dayMinutes = (day: MemberDay): number =>
    day.ranges.reduce((sum, range) => sum + rangeMinutes(range), 0);

/** Merge possibly-overlapping ranges into disjoint intervals, in minutes. */
export const mergeIntervals = (ranges: TimeRange[]): { start: number; end: number }[] => {
    const sorted = ranges
        .map(range => ({start: toMinutes(range.starts_at), end: toMinutes(range.ends_at)}))
        .filter(interval => interval.end > interval.start)
        .sort((a, b) => a.start - b.start);

    const merged: { start: number; end: number }[] = [];

    sorted.forEach(interval => {
        const last = merged[merged.length - 1];

        if (last && interval.start <= last.end) {
            last.end = Math.max(last.end, interval.end);
        } else {
            merged.push({...interval});
        }
    });

    return merged;
};

export type CoverageStatus = 'covered' | 'fragile' | 'uncovered' | 'closed';

export interface DayCoverage {
    status: CoverageStatus;
    /** Minutes of worked amplitude with no pharmacist, as intervals. */
    gaps: { start: number; end: number }[];
    /** Distinct members present at some point of the day. */
    headcount: number;
}

/** How many members are present at one minute of the day. */
const presentAt = (days: MemberDay[], minute: number): number =>
    days.filter(day => day.ranges.some(range =>
        toMinutes(range.starts_at) <= minute && minute < toMinutes(range.ends_at)
    )).length;

/**
 * Pharmacist coverage over one day: inside the worked amplitude (union of
 * every presence), a minute without any pharmacist is a gap; full coverage
 * by a single pharmacist at its thinnest point is fragile.
 */
export const dayCoverage = (days: MemberDay[]): DayCoverage => {
    const worked = days.filter(day => day.ranges.length);

    if (!worked.length) {
        return {status: 'closed', gaps: [], headcount: 0};
    }

    const amplitude = mergeIntervals(worked.flatMap(day => day.ranges));
    const pharmacists = worked.filter(day =>
        (day.member.rankId ? jobRoleForRank(day.member.rankId) : null) === 'pharmacist'
    );
    const pharmacistIntervals = mergeIntervals(pharmacists.flatMap(day => day.ranges));

    const gaps: { start: number; end: number }[] = [];

    amplitude.forEach(span => {
        let cursor = span.start;

        pharmacistIntervals
            .filter(interval => interval.end > span.start && interval.start < span.end)
            .forEach(interval => {
                if (interval.start > cursor) {
                    gaps.push({start: cursor, end: interval.start});
                }

                cursor = Math.max(cursor, interval.end);
            });

        if (cursor < span.end) {
            gaps.push({start: cursor, end: span.end});
        }
    });

    if (gaps.length) {
        return {status: 'uncovered', gaps, headcount: worked.length};
    }

    // Coverage is gapless here; sweep the pharmacist boundaries to find the
    // thinnest concurrent coverage — a relay of solo pharmacists is fragile.
    const checkpoints = amplitude.flatMap(span => [
        span.start,
        ...pharmacists
            .flatMap(day => day.ranges)
            .flatMap(range => [toMinutes(range.starts_at), toMinutes(range.ends_at)])
            .filter(minute => minute > span.start && minute < span.end),
    ]);

    const thinnest = Math.min(...checkpoints.map(minute => presentAt(pharmacists, minute)));

    return {
        status: thinnest <= 1 ? 'fragile' : 'covered',
        gaps: [],
        headcount: worked.length,
    };
};

export interface SlotShortfall {
    target: StaffingTarget;
    /** Worst missing headcount over the slot (positive number). */
    missing: number;
}

/**
 * Compare the presences of one day to its staffing targets: for each target
 * slot, the worst under-staffing found while sweeping the slot boundaries.
 */
export const dayShortfalls = (days: MemberDay[], targets: StaffingTarget[], dayOfWeek: number): SlotShortfall[] => {
    return targets
        .filter(target => target.day_of_week === dayOfWeek)
        .map(target => {
            const boundaries = new Set<number>([toMinutes(target.starts_at)]);

            days.flatMap(day => day.ranges).forEach(range => {
                [toMinutes(range.starts_at), toMinutes(range.ends_at)].forEach(minute => {
                    if (minute > toMinutes(target.starts_at) && minute < toMinutes(target.ends_at)) {
                        boundaries.add(minute);
                    }
                });
            });

            const missing = Math.max(...[...boundaries].map(minute => target.headcount - presentAt(days, minute)));

            return {target, missing};
        })
        .filter(shortfall => shortfall.missing > 0);
};

export const formatMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;

    return `${hours}h${rest ? String(rest).padStart(2, '0') : ''}`;
};
